import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["validator", "dirtyCheck", "transformValueToInput", "transformEventToValue", "onChange", "onFocus", "onBlur"],
    _excluded2 = ["namespace", "fieldName", "defaultValue"],
    _excluded3 = ["value"];
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BFMHooksContext } from './context';
import { defaultEventToValue, defaultValueToInput } from './helpers';
/**
 * Helper function to omit keys from a object.
 *
 * @param {object} original
 * @param {string[]} keysToOmit
 * @return {object}
 */

var omitKeys = function omitKeys(original, keysToOmit) {
  return (keysToOmit || []).reduce(function (result, key) {
    if (key in result) {
      delete result[key];
    }

    return result;
  }, original);
};
/**
 * @callback validatorCallback
 *
 * Function used to validate the input value.
 * Should return a falsy value if the input value is valid.
 * If the input value is not valid. any truthy value can be returned, for example a string of array with error message(s).
 *
 * @param {string|number|Array|Object|boolean} value - Input value, transformed from event or the defaultValue (if set)
 * @param {object} props - All props set on the input, except the callback functions: validator, dirtyCheck,
 *                         transformValueToInput, transformEventToValue, onChange, onFocus, onBlur.
 * @return {*}
 */

/**
 * @callback dirtyCheckCallback
 *
 * Function to check if the field is value has been modified (dirty).
 *
 * @param {string|number|Array|Object|boolean} newValue - The new input value, transformed from event
 * @param {string|number|Array|Object|boolean} valueOnFocus - The state value when the input got focus
 */

/**
 * @param {object} props
 * @param {string} props.namespace - The form namespace
 * @param {string} props.fieldName - The unique input field name within the namespace
 * @param {string} [props.defaultValue] - The default starting value of the field.
 * @param {validatorCallback} [props.validator]
 * @param {dirtyCheckCallback} [props.dirtyCheck]
 * @param {function(value)} [props.transformValueToInput] - Transform state value to input value.
 *                                                          When not given: the value will not be transformed.
 * @param {function(...args)} [props.transformEventToValue] - Transform onChange event to state value.
 *                                                          When not given: the event.target.value will be used.
 * @param {function(event)} [props.onFocus] - Called on an onFocus event. It can't be used to override the input prop.
 * @param {function(...args)} [props.onChange] - Called on an onChange event. It can't be used to override the input prop.
 * @param {function(event)} [props.onBlur] - Called on an onBlur event. It can't be used to override the input prop.
 * @param {string[]} [omitProps] - Prop keys that should not be returned.
 * @return {object} - Props for the input field.
 *                    Excluded are props only used for the hook: namespace, fieldName, defaultValue, validator, dirtyCheck, transformValueToInput and transformEventToValue
 *                    Excluded are keys given by the omitProps.
 *                    Included are onFocus, onChange and onBlur to handle the input field state.
 */


export var useConnectField = function useConnectField(props, omitProps) {
  var _useContext = useContext(BFMHooksContext),
      blurField = _useContext.blurField,
      changeField = _useContext.changeField,
      defaultValueField = _useContext.defaultValueField,
      focusField = _useContext.focusField,
      initField = _useContext.initField,
      removeField = _useContext.removeField,
      subscribeToField = _useContext.subscribeToField;

  var validator = props.validator,
      dirtyCheck = props.dirtyCheck,
      _props$transformValue = props.transformValueToInput,
      transformValueToInput = _props$transformValue === void 0 ? defaultValueToInput : _props$transformValue,
      _props$transformEvent = props.transformEventToValue,
      transformEventToValue = _props$transformEvent === void 0 ? defaultEventToValue : _props$transformEvent,
      onChange = props.onChange,
      onFocus = props.onFocus,
      onBlur = props.onBlur,
      staticProps = _objectWithoutPropertiesLoose(props, _excluded); // For storing static props see bellow.


  var propsRef = useRef(null); // Hook specific props.

  var namespace = staticProps.namespace,
      fieldName = staticProps.fieldName,
      _staticProps$defaultV = staticProps.defaultValue,
      defaultValue = _staticProps$defaultV === void 0 ? '' : _staticProps$defaultV,
      otherProps = _objectWithoutPropertiesLoose(staticProps, _excluded2); // Throw an error if namespace and/or fieldName changes after first rendering, because it's not supported
  // Dynamically changing these values can result in strange side effects. It's better to render a new component.


  var namesRef = useRef({
    namespace: namespace,
    fieldName: fieldName
  });
  useEffect(function () {
    if (namesRef.current.namespace !== namespace || namesRef.current.fieldName !== fieldName) {
      throw new Error('Changing the namespace and/or fieldName of an already rendered component is not supported.');
    }
  }, [namespace, fieldName]); // Store static props for use in the validator callback.
  // This way you can (re-)use for example: required, minlength, maxlength, etc. in the validator

  propsRef.current = staticProps;
  var getError = useCallback(function (_value) {
    return validator && validator(_value, propsRef.current);
  }, [validator]); // init field and state on first rendering

  var _useState = useState(function () {
    return initField(namespace, fieldName, defaultValue, getError(defaultValue));
  }),
      _useState$ = _useState[0],
      value = _useState$.value,
      fieldProps = _objectWithoutPropertiesLoose(_useState$, _excluded3),
      setFieldState = _useState[1];

  useEffect(function () {
    var unsubscribe = subscribeToField(namespace, fieldName, setFieldState); // unsubscribe and remove field on unmount

    return function () {
      unsubscribe();
      removeField(namespace, fieldName);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // update defaultValue on change, see `defaultValueField` function for more info

  useEffect(function () {
    defaultValueField(namespace, fieldName, defaultValue, getError(defaultValue));
  }, [defaultValue, defaultValueField, fieldName, getError, namespace]);

  var _props = omitKeys(_extends({}, otherProps, fieldProps), omitProps);

  _props.value = transformValueToInput(value);
  _props.onFocus = useCallback(function (event) {
    focusField(namespace, fieldName);
    onFocus && onFocus(event);
  }, [fieldName, namespace, onFocus, focusField]);
  _props.onChange = useCallback(function () {
    var value = transformEventToValue.apply(void 0, arguments);
    var error = getError(value);
    changeField(namespace, fieldName, value, error, dirtyCheck);
    onChange && onChange.apply(void 0, arguments);
  }, [transformEventToValue, getError, changeField, namespace, fieldName, dirtyCheck, onChange]);
  _props.onBlur = useCallback(function (event) {
    blurField(namespace, fieldName);
    onBlur && onBlur(event);
  }, [fieldName, namespace, onBlur, blurField]);
  return _props;
};